import * as cocoSsd from "@tensorflow-models/coco-ssd";
import { ensureTfBackend } from "./tfBackend.js";

import { devConfig } from "./appConfig.js";
import { buildStableKeyedItems, itemKey } from "./lockTrackKeys.js";
import {
  noteInferenceDuration,
  noteInferenceFailure,
  noteInferenceSuccess,
} from "./liveHealth.js";
import {
  formatRangeDotSuffix,
  resolveRangeDisplay,
} from "./objectRange.js";

/**
 * @typedef {{
 *   video: HTMLVideoElement,
 *   overlay: HTMLCanvasElement,
 *   onTargetEvent?: (evt: {
 *     type: "detected" | "lock";
 *     className: string;
 *     score: number;
 *     distanceM: number | null;
 *     showDistance: boolean;
 *   }) => void
 * }} PersonDetectionOpts
 */

const BOX_STYLE = {
  person: {
    stroke: "rgba(94, 233, 132, 0.95)",
    glow: "rgba(94, 233, 132, 0.45)",
    tagBg: "rgba(94, 233, 132, 0.92)",
    tagFg: "#061208",
  },
  cat: {
    stroke: "rgba(255, 170, 110, 0.95)",
    glow: "rgba(255, 170, 110, 0.4)",
    tagBg: "rgba(255, 170, 110, 0.92)",
    tagFg: "#1a0a04",
  },
  dog: {
    stroke: "rgba(130, 200, 255, 0.95)",
    glow: "rgba(130, 200, 255, 0.4)",
    tagBg: "rgba(130, 200, 255, 0.92)",
    tagFg: "#061018",
  },
};

/**
 * クラス名から安定した色相（同じクラスは常に同じ色）
 * @param {string} className
 */
function hslStyleForClass(className) {
  let h = 2166136261;
  for (let i = 0; i < className.length; i++) {
    h ^= className.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = (h >>> 0) % 360;
  return {
    stroke: `hsla(${hue}, 72%, 58%, 0.95)`,
    glow: `hsla(${hue}, 72%, 48%, 0.38)`,
    tagBg: `hsla(${hue}, 42%, 30%, 0.92)`,
    tagFg: "#f2fff6",
  };
}

/**
 * @param {string} className
 */
function boxStyleFor(className) {
  const preset =
    BOX_STYLE[/** @type {keyof typeof BOX_STYLE} */ (className)];
  return preset ?? hslStyleForClass(className);
}

/**
 * 正立矩形の 4 コーナーに L 字（枠線の代わり）
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} armRel min(w,h) に対するアーム長の比率
 */
function drawCornerBrackets(
  ctx,
  x,
  y,
  w,
  h,
  armRel,
  stroke,
  glow,
  lineWidth
) {
  const L = Math.min(w * armRel, h * armRel, 56);
  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.lineJoin = "miter";
  ctx.lineCap = "square";
  ctx.shadowColor = glow;
  ctx.shadowBlur = 14;
  ctx.beginPath();
  ctx.moveTo(x, y + L);
  ctx.lineTo(x, y);
  ctx.lineTo(x + L, y);
  ctx.moveTo(x + w - L, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + L);
  ctx.moveTo(x + w, y + h - L);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w - L, y + h);
  ctx.moveTo(x + L, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + h - L);
  ctx.stroke();
  ctx.restore();
}

/**
 * 中心(cx,cy)・45° 回転した正方形の 4 コーナーブラケット（ロックオン ◇）
 */
function drawRotatedSquareBrackets(
  ctx,
  cx,
  cy,
  side,
  armRel,
  stroke,
  glow,
  lineWidth
) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI / 4);
  drawCornerBrackets(
    ctx,
    -side / 2,
    -side / 2,
    side,
    side,
    armRel,
    stroke,
    glow,
    lineWidth
  );
  ctx.restore();
}

/**
 * @param {unknown} tc
 * @returns {Set<string> | null} null = クラスで絞らない
 */
function classFilterSet(tc) {
  if (tc === "all") return null;
  if (!Array.isArray(tc) || tc.length === 0) return null;
  return new Set(tc);
}

let modelPromise = /** @type {Promise<cocoSsd.ObjectDetection> | null} */ (null);
/** @type {string | null} */
let loadedSsdBase = null;

export function ensureModel() {
  const base = devConfig.detection.ssdBase;
  if (modelPromise && loadedSsdBase !== base) {
    modelPromise = null;
    loadedSsdBase = null;
  }
  if (!modelPromise) {
    modelPromise = (async () => {
      await ensureTfBackend();
      loadedSsdBase = base;
      return cocoSsd.load({ base });
    })();
  }
  return modelPromise;
}

/**
 * object-fit: cover と同じ切り抜きで video を矩形へ描画
 * @returns {boolean}
 */
export function drawVideoCover(ctx, video, destW, destH) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return false;
  const scale = Math.max(destW / vw, destH / vh);
  const sw = destW / scale;
  const sh = destH / scale;
  const sx = (vw - sw) / 2;
  const sy = (vh - sh) / 2;
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, destW, destH);
  return true;
}

/** @type {number} */
let loopTimer = 0;
/** @type {boolean} */
let running = false;
/** @type {HTMLCanvasElement | null} */
let workCanvas = null;
/** @type {CanvasRenderingContext2D | null} */
let workCtx = null;
/** @type {(() => void) | null} */
let boundResize = null;

/**
 * @param {PersonDetectionOpts} opts
 */
export async function startPersonDetection(opts) {
  const { video, overlay, onTargetEvent } = opts;
  stopPersonDetection(overlay);

  /** @type {cocoSsd.ObjectDetection} */
  const model = await ensureModel();
  running = true;

  /** キーごとの追従レティクル中心（画面座標 px） */
  /** @type {Map<string, { x: number; y: number }>} */
  const trackByKey = new Map();
  /** キーごとのロックゾーン滞在開始時刻（performance.now） */
  /** @type {Map<string, number>} */
  const dwellStartByKey = new Map();
  /** @type {Set<string>} */
  let prevActiveKeys = new Set();
  /** @type {Set<string>} */
  const lockAnnouncedKeys = new Set();
  /** 安定キー照合用：各キー直近の検出中心（画面 px） */
  /** @type {Map<string, { cx: number; cy: number }>} */
  const lastCentersByKey = new Map();

  if (!workCanvas) {
    workCanvas = document.createElement("canvas");
    workCtx = workCanvas.getContext("2d", { willReadFrequently: true });
  }

  const overlayCtx = overlay.getContext("2d");
  if (!workCtx || !overlayCtx) return;

  function detectSize() {
    const iw = window.innerWidth;
    const ih = window.innerHeight;
    let dw = iw;
    let dh = ih;
    const maxSideLimit = devConfig.detection.maxSidePx;
    const maxSide = Math.max(dw, dh);
    if (maxSide > maxSideLimit) {
      const s = maxSideLimit / maxSide;
      dw = Math.round(dw * s);
      dh = Math.round(dh * s);
    }
    return { dw, dh, iw, ih };
  }

  function resizeOverlay() {
    const dpr = Math.min(window.devicePixelRatio ?? 1, 2);
    const iw = window.innerWidth;
    const ih = window.innerHeight;
    overlay.width = Math.floor(iw * dpr);
    overlay.height = Math.floor(ih * dpr);
    overlay.style.width = `${iw}px`;
    overlay.style.height = `${ih}px`;
    overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  async function runFrame() {
    if (!running || !video.srcObject) return;

    const intervalMs = devConfig.detection.intervalMs ?? 180;
    const t0 = performance.now();
    try {
      await runFrameBody();
      noteInferenceSuccess();
    } catch (e) {
      console.error(e);
      noteInferenceFailure();
    } finally {
      noteInferenceDuration("ssd", performance.now() - t0, intervalMs);
    }
  }

  async function runFrameBody() {
    const { dw, dh, iw, ih } = detectSize();

    workCanvas.width = dw;
    workCanvas.height = dh;
    workCtx.fillStyle = "#000";
    workCtx.fillRect(0, 0, dw, dh);

    if (!drawVideoCover(workCtx, video, dw, dh)) {
      overlayCtx.clearRect(0, 0, iw, ih);
      return;
    }

    const scoreMin = devConfig.detection.scoreMin;
    const lockQuant = devConfig.detection.lockTrackQuantPx ?? 32;
    const maxBoxes = devConfig.detection.maxBoxes ?? 60;
    const preds = await model.detect(workCanvas, maxBoxes, scoreMin);
    resizeOverlay();

    const sx = iw / dw;
    const sy = ih / dh;

    overlayCtx.clearRect(0, 0, iw, ih);

    const zoneR = devConfig.detection.lockZoneScreenRatio ?? 0.3;
    const longSide = Math.max(iw, ih);
    const side = longSide * zoneR;
    const zx = (iw - side) / 2;
    const zy = (ih - side) / 2;

    const allowed = classFilterSet(devConfig.detection.targetClasses);

    /** @type {{ p: cocoSsd.DetectedObject; x: number; y: number; w: number; h: number }[]} */
    const items = [];
    for (const p of preds) {
      if (allowed && !allowed.has(p.class)) continue;
      const [bx, by, bw, bh] = p.bbox;
      const x = bx * sx;
      const y = by * sy;
      const w = bw * sx;
      const h = bh * sy;
      items.push({ p, x, y, w, h });
    }

    /** 中央照準と同基準（22vmin / 110px cap）に対し 2 段階ほど小さい内側レティクル */
    const vmin = Math.min(iw, ih);
    const reticleRefPx = Math.min(vmin * 0.22, 110);
    const innerScale = devConfig.detection.lockInnerReticleScale ?? 0.45;
    let innerSide = reticleRefPx * innerScale;
    innerSide = Math.min(innerSide, Math.max(24, side - 6));

    const halfInner = innerSide / 2;
    const lockMax = devConfig.detection.lockMaxConcurrent ?? 4;
    const zone = { x: zx, y: zy, side };

    /** @type {typeof items} */
    const inZoneAll = [];
    for (const it of items) {
      const mcx = it.x + it.w / 2;
      const mcy = it.y + it.h / 2;
      if (
        mcx >= zx &&
        mcx <= zx + side &&
        mcy >= zy &&
        mcy <= zy + side
      ) {
        inZoneAll.push(it);
      }
    }
    inZoneAll.sort((a, b) => b.p.score - a.p.score);
    /** @type {typeof items} */
    const inZoneTop = [];
    const seenKey = new Set();
    for (const it of inZoneAll) {
      const k = itemKey(it, lockQuant);
      if (seenKey.has(k)) continue;
      seenKey.add(k);
      inZoneTop.push(it);
      if (inZoneTop.length >= lockMax) break;
    }

    const keyedItems = buildStableKeyedItems(
      inZoneTop,
      prevActiveKeys,
      lastCentersByKey,
      {
        lockMatchMaxDistPx: devConfig.detection.lockMatchMaxDistPx,
        lockTrackQuantPx: lockQuant,
      },
    );
    const activeKeys = new Set(keyedItems.map((e) => e.key));
    for (const k of [...trackByKey.keys()]) {
      if (!activeKeys.has(k)) trackByKey.delete(k);
    }
    for (const k of [...dwellStartByKey.keys()]) {
      if (!activeKeys.has(k)) dwellStartByKey.delete(k);
    }
    for (const k of [...lockAnnouncedKeys]) {
      if (!activeKeys.has(k)) lockAnnouncedKeys.delete(k);
    }

    const now = performance.now();
    const dwellMs = devConfig.detection.lockDwellMs ?? 2000;
    const dt = devConfig.detection.intervalMs ?? 180;
    const tau = devConfig.detection.lockTrackTauMs ?? 320;
    const alpha = Math.min(1, 1 - Math.exp(-dt / tau));

    /** @type {{ key: string; it: (typeof items)[0]; wantX: number; wantY: number; trackX: number; trackY: number; isLockOn: boolean }[]} */
    const lockSlots = [];
    for (const { it, key } of keyedItems) {
      const targetCx = it.x + it.w / 2;
      const targetCy = it.y + it.h / 2;
      const rangeDisp = resolveRangeDisplay({ targetCx, targetCy, zone });
      if (!prevActiveKeys.has(key)) {
        onTargetEvent?.({
          type: "detected",
          className: it.p.class,
          score: it.p.score,
          distanceM: rangeDisp.distanceM,
          showDistance: rangeDisp.showDistance,
        });
      }
      let wantX = it.x + it.w / 2;
      let wantY = it.y + it.h / 2;
      wantX = Math.min(
        Math.max(wantX, zx + halfInner),
        zx + side - halfInner,
      );
      wantY = Math.min(
        Math.max(wantY, zy + halfInner),
        zy + side - halfInner,
      );

      let tr = trackByKey.get(key);
      if (!tr) {
        tr = { x: wantX, y: wantY };
        trackByKey.set(key, tr);
      } else {
        tr.x += (wantX - tr.x) * alpha;
        tr.y += (wantY - tr.y) * alpha;
        tr.x = Math.min(
          Math.max(tr.x, zx + halfInner),
          zx + side - halfInner,
        );
        tr.y = Math.min(
          Math.max(tr.y, zy + halfInner),
          zy + side - halfInner,
        );
      }

      if (!dwellStartByKey.has(key)) {
        dwellStartByKey.set(key, now);
      }
      const t0 = dwellStartByKey.get(key) ?? now;
      const isLockOn = now - t0 >= dwellMs;
      if (isLockOn && !lockAnnouncedKeys.has(key)) {
        lockAnnouncedKeys.add(key);
        onTargetEvent?.({
          type: "lock",
          className: it.p.class,
          score: it.p.score,
          distanceM: rangeDisp.distanceM,
          showDistance: rangeDisp.showDistance,
        });
      }
      lockSlots.push({
        key,
        it,
        wantX,
        wantY,
        trackX: tr.x,
        trackY: tr.y,
        isLockOn,
      });
    }

    lastCentersByKey.clear();
    for (const { it, key } of keyedItems) {
      lastCentersByKey.set(key, {
        cx: it.x + it.w / 2,
        cy: it.y + it.h / 2,
      });
    }

    drawCornerBrackets(
      overlayCtx,
      zx,
      zy,
      side,
      side,
      0.1,
      "rgba(94, 233, 132, 0.72)",
      "rgba(94, 233, 132, 0.42)",
      1.85
    );

    for (const it of items) {
      const { p, x, y, w, h } = it;
      const sty = boxStyleFor(p.class);
      overlayCtx.strokeStyle = sty.stroke;
      overlayCtx.lineWidth = 2;
      overlayCtx.shadowColor = sty.glow;
      overlayCtx.shadowBlur = 10;
      overlayCtx.strokeRect(x, y, w, h);
      overlayCtx.shadowBlur = 0;

      overlayCtx.font =
        '600 11px "VT323", "DotGothic16", system-ui, sans-serif';
      const targetCx = x + w / 2;
      const targetCy = y + h / 2;
      const rangeDisp = resolveRangeDisplay({ targetCx, targetCy, zone });
      const label = `${String(p.class).toUpperCase()} ${Math.round(p.score * 100)}%${formatRangeDotSuffix(rangeDisp)}`;
      const pad = 4;
      const tw = overlayCtx.measureText(label).width;
      overlayCtx.fillStyle = sty.tagBg;
      overlayCtx.fillRect(x, Math.max(0, y - 18), tw + pad * 2, 16);
      overlayCtx.fillStyle = sty.tagFg;
      overlayCtx.fillText(label, x + pad, Math.max(12, y - 5));
    }

    for (const slot of lockSlots) {
      const { trackX, trackY, isLockOn } = slot;
      const ix = trackX - innerSide / 2;
      const iy = trackY - innerSide / 2;
      const innerStroke = isLockOn
        ? "rgba(255, 72, 58, 0.96)"
        : "rgba(200, 255, 220, 0.92)";
      const innerGlow = isLockOn
        ? "rgba(255, 42, 32, 0.52)"
        : "rgba(94, 233, 132, 0.48)";
      drawCornerBrackets(
        overlayCtx,
        ix,
        iy,
        innerSide,
        innerSide,
        0.12,
        innerStroke,
        innerGlow,
        isLockOn ? 1.75 : 1.55
      );

      if (isLockOn) {
        const fit = Math.min(
          1,
          devConfig.detection.lockMicroDiamondFitRatio ?? 1,
        );
        const microSide = Math.max(
          8,
          (innerSide / Math.SQRT2) * fit,
        );
        drawRotatedSquareBrackets(
          overlayCtx,
          trackX,
          trackY,
          microSide,
          0.14,
          "rgba(255, 108, 92, 0.94)",
          "rgba(255, 60, 48, 0.45)",
          1.35
        );
      }
    }
    prevActiveKeys = activeKeys;
  }

  function scheduleLoop() {
    loopTimer = window.setTimeout(async () => {
      if (!running) return;
      try {
        await runFrame();
      } catch (e) {
        console.error(e);
      }
      if (running) scheduleLoop();
    }, devConfig.detection.intervalMs);
  }

  boundResize = () => {
    resizeOverlay();
  };
  window.addEventListener("resize", boundResize);

  resizeOverlay();
  scheduleLoop();
}

/**
 * @param {HTMLCanvasElement | null} [overlay]
 */
export function stopPersonDetection(overlay) {
  running = false;
  if (loopTimer) {
    clearTimeout(loopTimer);
    loopTimer = 0;
  }
  if (boundResize) {
    window.removeEventListener("resize", boundResize);
    boundResize = null;
  }
  if (overlay) {
    const ctx = overlay.getContext("2d");
    if (ctx) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, overlay.width, overlay.height);
    }
  }
}
